import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { router } from './routes/Routes';
import { RouterProvider } from 'react-router-dom';
import { RootState } from './store/store';
import { useSelector } from 'react-redux';
import { Capacitor } from '@capacitor/core';
import { getActiveImpersonation, updateCurrentUserInfo } from './services/auth';
import { useThemeHotkey } from './hooks/useThemeHotkey';
import { usePlainOraViewHotkey } from './mcgrawHill/usePlainOraViewHotkey'; // mcgraw-hill
import { GamepadProvider } from './context/GamepadProvider';
import {
  clearPushNotificationSession,
  initializePushNotifications,
} from './services/pushNotifications';
import { useIsDarkTheme } from './hooks/useIsDarkTheme';
import {
  getOraCookieAcceptCookie,
  setOraCookieAcceptCookie,
} from './utils/cookiesUtils';
import { fetchGeneralUserStats } from './services/stats';
import SubscriptionProvider from './providers/SubscriptionProvider';
import VideoViewerModalProvider from './providers/VideoViewerModalProvider';
import SessionFeedbackProvider from './providers/SessionFeedbackProvider';
import AIDrawerProvider from './providers/AIDrawerProvider';
import { getCookie } from './utils/getCookie';
import moment from 'moment';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar } from '@capacitor/status-bar';
import { examsSelectors } from './slices/exams/examsSlice';
import OfflineIndicator from './components/ora/Global/OfflineIndicator';
import BackendOverrideBadge from './components/dev/BackendOverrideBadge';
import { isBackendSwitcherEnabled } from './config/backendUrl';
import PaymentIssueBanner from './components/ora/Global/PaymentIssueBanner';
import AnkiImportNotifierProvider from './providers/AnkiImportNotifierProvider';
import ClientVersionManager from './components/ora/Global/ClientVersionManager';
import ClientUpdateRequiredModal from './components/ora/Global/ClientUpdateRequiredModal';
import InstitutionalAccessModals from './components/ora/Global/InstitutionalAccessModals';
import {
  captureFirstTouchAttributionFromCurrentUrl,
  isSketchyPartnershipAttribution,
  resolveFirstTouchAttribution,
  toPosthogUtmProperties,
} from './utils/attribution';
import { scheduleSessionIntercomBoot } from './utils/intercom';
import { withPosthogAfterIdle } from './utils/posthogClient';
import { SHELL_INTERCOM_SETTINGS } from './components/ora/Global/CompanionShell/useDragPosition';
import AssetLoadErrorBoundary from './components/ora/ErrorBoundary/AssetLoadErrorBoundary';
import AudioOverviewBarHost from './audioOverview/bar/AudioOverviewBarHost';
import AudioOverviewWatcher from './audioOverview/watcher/AudioOverviewWatcher';
import NativeAuthUrlListener from './components/ora/Auth/NativeAuthUrlListener/NativeAuthUrlListener';
import { registerAuthNavigator } from './services/authNavigation';
import { applyInstanceToBody } from './instances/applyInstanceToBody';
import { useInstance } from './instances/useInstance';

const ExplainerPopoversManager = lazy(
  () => import('./components/ora/Global/ExplainerPopoversManager')
);

const APP_NAME = 'Ora AI';

// Auth code outside React (the axios client, the native deep-link handler)
// navigates through the app's router rather than reloading the page.
registerAuthNavigator((path, options) => {
  void router.navigate(path, options);
});

const openAudioOverviewLibrary = () => {
  void router.navigate('/audio-overviews');
};

const openAudioOverviewArticle = (articleId: string) => {
  void router.navigate(`/library/${encodeURIComponent(articleId)}`);
};

const resolveDocumentTitle = (pathname: string) => {
  if (pathname === '/') return `${APP_NAME}`;
  if (pathname === '/dashboard') return `${APP_NAME} | Dashboard`;
  if (pathname === '/onboarding') return `${APP_NAME} | Onboarding`;
  if (pathname === '/admin' || pathname.startsWith('/admin/'))
    return `${APP_NAME} | Admin`;
  if (pathname.startsWith('/school/')) return `${APP_NAME} | School`;
  if (pathname.startsWith('/study/qbank')) return `${APP_NAME} | QBank Study`;
  if (pathname === '/study/flashcards') return `${APP_NAME} | Flashcards`;
  if (pathname === '/study/video') return `${APP_NAME} | Video Study`;
  if (pathname === '/study/summary') return `${APP_NAME} | Session Summary`;
  if (pathname.startsWith('/study/question/'))
    return `${APP_NAME} | Practice Question`;
  if (pathname === '/account') return `${APP_NAME} | Account`;
  if (pathname === '/performance') return `${APP_NAME} | Performance`;
  if (pathname === '/flashcards' || pathname.startsWith('/flashcards/')) {
    return `${APP_NAME} | Flashcard Decks`;
  }
  if (pathname === '/upload-deck') return `${APP_NAME} | Upload Deck`;
  if (pathname === '/library' || pathname.startsWith('/library/'))
    return `${APP_NAME} | Library`;
  if (pathname.startsWith('/topics')) return `${APP_NAME} | Topics`;
  if (pathname.startsWith('/videos')) return `${APP_NAME} | Videos`;
  if (pathname === '/chat') return `${APP_NAME} | Chat`;
  if (pathname === '/audio-overviews') return `${APP_NAME} | Audio Overviews`;
  if (pathname === '/games') return `${APP_NAME} | Games`;
  if (pathname.startsWith('/games/doctordle')) return `${APP_NAME} | Doctordle`;
  if (pathname === '/games/daily-question')
    return `${APP_NAME} | Daily Question`;
  if (pathname.startsWith('/games/start/')) return `${APP_NAME} | Game Start`;
  if (pathname === '/games/buzzwords') return `${APP_NAME} | Buzzwords`;
  if (pathname === '/games/differential') return `${APP_NAME} | Differential`;
  if (pathname === '/games/survival') return `${APP_NAME} | Survival`;
  if (pathname === '/qbank-start') return `${APP_NAME} | QBank Start`;
  if (pathname === '/email-preferences')
    return `${APP_NAME} | Email Preferences`;
  if (pathname === '/verify-email' || pathname.startsWith('/verify-email/')) {
    return `${APP_NAME} | Verify Email`;
  }
  if (pathname === '/pricing') return `${APP_NAME} | Pricing`;
  if (pathname === '/help' || pathname.startsWith('/help/')) {
    return `${APP_NAME} | Help`;
  }
  if (pathname === '/auth') return `${APP_NAME} | Authentication`;
  if (pathname === '/auth/2fa')
    return `${APP_NAME} | Two-Factor Authentication`;
  if (pathname.startsWith('/invite/')) return `${APP_NAME} | Invite`;
  if (pathname.startsWith('/reset')) return `${APP_NAME} | Reset Password`;
  return APP_NAME;
};

function App() {
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const instance = useInstance();
  const previousInstanceSlug = useRef<string>();

  const subscription = useSelector(
    (state: RootState) => state.subscription.subscription
  );

  const school = useSelector((state: RootState) => state.school.school);
  const primaryExam = useSelector(examsSelectors.selectPrimaryExam);

  useThemeHotkey();
  usePlainOraViewHotkey(); // mcgraw-hill

  // The theme the student sees: an instance's forced theme, else their own.
  const isDarkTheme = useIsDarkTheme();

  const [pathname, setPathname] = useState(
    () => router.state.location.pathname
  );

  useEffect(() => {
    const unsubscribe = router.subscribe((state) => {
      const nextPathname = state.location.pathname;

      setPathname((previousPathname) =>
        previousPathname === nextPathname ? previousPathname : nextPathname
      );
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    document.title = resolveDocumentTitle(pathname);
  }, [pathname]);

  useEffect(() => {
    captureFirstTouchAttributionFromCurrentUrl();

    if (process.env.NODE_ENV !== 'production') return;

    return withPosthogAfterIdle((posthog) => {
      const { attribution, origin } = resolveFirstTouchAttribution();
      const utmProps = toPosthogUtmProperties(attribution);
      if (Object.keys(utmProps).length === 0) return;

      // Only explicit UTMs become sticky super-properties; an inferred
      // direct/organic pair must not pin itself onto every later event.
      if (!attribution?.inferred) posthog.register_once(utmProps);

      const source = attribution?.utmSource || 'unknown';
      const campaign = attribution?.utmCampaign || 'none';
      const sessionKey = `ora-attribution-detected:${source}:${campaign}`;

      if (!sessionStorage.getItem(sessionKey)) {
        posthog.capture('app_attribution_detected', {
          ...utmProps,
          partner_source: source,
          attribution_origin: origin,
          attribution_inferred: attribution?.inferred ? 'true' : 'false',
        });

        sessionStorage.setItem(sessionKey, '1');
      }
    });
  }, []);

  // Setup Posthog for authenticated users
  useEffect(() => {
    const setupPosthogUserFeatures = () =>
      withPosthogAfterIdle((posthog) => {
        const { attribution } = resolveFirstTouchAttribution();
        const utmParams = toPosthogUtmProperties(attribution);
        // Same rule as the anonymous path: inferred values never become
        // sticky super-properties.
        if (Object.keys(utmParams).length > 0 && !attribution?.inferred) {
          posthog.register_once(utmParams);
        }

        const cookiesAccepted = getOraCookieAcceptCookie();

        if (currentUser && currentUser?.email) {
          if (cookiesAccepted !== 'accepted') {
            setOraCookieAcceptCookie('accepted');
          }

          // Identify user in PostHog
          posthog.identify(currentUser.id, {
            email: currentUser.email,
            name: currentUser.name,
            created_at: currentUser.createdAt,
            role: currentUser.role,
            isPeerAdvocate: currentUser.ambassador,
            has_ios_app: currentUser.hasIOSApp,
            has_android_app: currentUser.hasAndroidApp,
            cohort: currentUser.cohort,
            grandfathered: currentUser.grandfathered,
            isActivated: currentUser.isActivated,
            activatedAt: currentUser.activatedAt,
            activationReason: currentUser.activationReason,
            study_reminders: currentUser.studyReminders,
            subscribed_to_emails: currentUser.subscribedToEmails,
            school: school?.id === currentUser.schoolId ? school?.name : null,
            first_partner_source: currentUser.attributionSource,
            first_utm_campaign: currentUser.attributionCampaign,
            sketchy_discount_eligible: isSketchyPartnershipAttribution({
              utmCampaign: currentUser.attributionCampaign,
            }),
            // mcgraw-hill: the real instance, not the plain Ora view's.
            instance: currentUser.instance?.slug ?? 'default',
          });

          // No Filters, No Excludes
          posthog.startSessionRecording();
        } else {
          console.log('Posthog Not Initialized');
          if (cookiesAccepted === 'rejected') {
            console.log('Non Essential Cookies Rejected');
          }
        }
      });

    const isImpersonating = !!getActiveImpersonation();
    if (!isImpersonating && currentUser?.email) {
      return setupPosthogUserFeatures();
    }
  }, [currentUser, school?.id, school?.name]);

  // Hide Android status bar on startup
  useEffect(() => {
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
      const hideStatusBar = async () => {
        try {
          await StatusBar.setOverlaysWebView({ overlay: true });
          await StatusBar.hide();
        } catch {
          // noop
        }
      };

      void hideStatusBar();
    }
  }, []);

  useEffect(() => {
    let cancelIntercomBoot: (() => void) | undefined;
    // An impersonating admin is not the student: Intercom stays anonymous
    // and PostHog is not told (ORA-1089).
    const isImpersonating = Boolean(getActiveImpersonation());

    if (currentUser && currentUser.email) {
      cancelIntercomBoot = scheduleSessionIntercomBoot({
        isImpersonating,
        user: {
          api_base: 'https://api-iam.intercom.io',
          app_id: 'x6oeppkc',
          source: 'ora',
          name: currentUser.name || '',
          email: currentUser.email,
          user_id: currentUser.id,
          created_at: currentUser.createdAt,
          role: currentUser.role,
          isPeerAdvocate: currentUser.ambassador,
          has_ios_app: currentUser.hasIOSApp,
          has_android_app: currentUser.hasAndroidApp,
          cohort: currentUser.cohort,
          grandfathered: currentUser.grandfathered,
          subscription_status: subscription?.status,
          subscription_plan: subscription?.plan,
          subscription_access_level: subscription?.accessLevel,
          study_reminders: currentUser.studyReminders,
          subscribed_to_emails: currentUser.subscribedToEmails,
          school: school?.id === currentUser.schoolId ? school?.name : null,
          primaryExam: primaryExam?.name,
          // On the floating shell the bubble is the only support entry point, so
          // Intercom's own launcher stands down and its Messenger opens in the
          // same corner (ORA-646).
          ...SHELL_INTERCOM_SETTINGS,
        },
      });

      fetchGeneralUserStats();

      const oraHc = getCookie('ora-hc');
      if (oraHc === 'true') {
        if (
          !isImpersonating &&
          currentUser.createdAt &&
          moment(currentUser.createdAt).isSame(moment(), 'day')
        ) {
          withPosthogAfterIdle((posthog) => {
            posthog.identify(currentUser.id, {
              hc: true,
            });
          });
        }

        // Remove the cookie by setting it to expire in the past
        document.cookie =
          'ora-hc=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      }

      if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
        void initializePushNotifications(currentUser.id, (link) => {
          void router.navigate(link);
        });

        if (!currentUser.hasIOSApp) {
          userOnApp('ios');
        }
      } else if (
        Capacitor.isNativePlatform() &&
        Capacitor.getPlatform() === 'android'
      ) {
        if (!currentUser.hasAndroidApp) {
          userOnApp('android');
        }
      }
    } else {
      // Clear any prior identified session, then boot anonymously so chat
      // still works on logged-out pages (auth, unlock, etc.).
      cancelIntercomBoot = scheduleSessionIntercomBoot({
        user: null,
        isImpersonating,
      });

      clearPushNotificationSession();
    }

    if (currentUser && currentUser.demoMode) {
      document.body.classList.add('DEMO-MODE');
    } else {
      document.body.classList.remove('DEMO-MODE');
    }

    return () => cancelIntercomBoot?.();
  }, [
    currentUser,
    primaryExam?.name,
    school?.id,
    school?.name,
    subscription?.accessLevel,
    subscription?.plan,
    subscription?.status,
  ]);

  useEffect(() => {
    previousInstanceSlug.current = applyInstanceToBody(
      document.body,
      instance,
      previousInstanceSlug.current
    );
  }, [instance]);

  // If we reloaded due to version update, hide splash after mount
  useEffect(() => {
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
      const flagged = localStorage.getItem('oraReloadSplash');
      if (flagged === '1') {
        setTimeout(() => {
          try {
            SplashScreen.hide();
          } catch {
            // noop
          }
          localStorage.removeItem('oraReloadSplash');
        }, 200);
      }
    }
  }, []);

  // Set Theme
  useEffect(() => {
    // High Contrast is Global
    if (currentUser && currentUser?.highContrast) {
      document.body.classList.add('theme-high-contrast');
    } else {
      document.body.classList.remove('theme-high-contrast');
    }

    // A fullscreen game round forces its own dark surface regardless of the
    // user's theme (see useOsWindowVariantOverride). While it does, it owns the
    // `theme-dark` class and flags the body; don't fight it here, or a
    // mid-session re-run of this effect (systemTheme/profile change) would strip
    // the class and revert it to a washed-out light rendering.
    if (document.body.dataset.forceDarkTheme) {
      return;
    }

    // Don't manage theme-dark class on exam pages - let the Exam component handle it
    if (
      pathname === '/study/qbank/block' ||
      pathname === '/study/qbank/block/review'
    ) {
      return;
    }

    if (isDarkTheme && pathname !== '/study/qbank/block') {
      document.body.classList.add('theme-dark');
    } else {
      if (!currentUser && pathname.includes('/help')) {
        return;
      }

      // Guests have no theme of their own, so the public games keep their
      // dark look. A signed-in student sees the games hub, start screens,
      // Doctordle and the daily question in their theme; a round in progress
      // forces dark on its own (`useOsWindowVariantOverride`).
      const isGuestGamesPage =
        !currentUser &&
        (pathname === '/games' ||
          pathname === '/games/daily-question' ||
          pathname.startsWith('/games/doctordle'));

      if (
        pathname === '/pricing' ||
        pathname === '/daily' ||
        pathname === '/auth' ||
        pathname === '/auth/2fa' ||
        isGuestGamesPage ||
        // Public school event flow (/play/[school] board + guest game) is
        // always the branded dark surface, for guests and members alike.
        pathname.startsWith('/play/') ||
        pathname.includes('/reset') ||
        pathname === '/study/summary' ||
        pathname === '/onboarding'
      ) {
        document.body.classList.add('theme-dark');
      } else {
        document.body.classList.remove('theme-dark');
      }
    }
  }, [currentUser, pathname, isDarkTheme]);

  // Add classes based on platform
  if (Capacitor.getPlatform() === 'ios') {
    document.body.classList.add('CAPACITOR-IOS');
  }
  if (Capacitor.getPlatform() === 'android') {
    document.body.classList.add('CAPACITOR-ANDROID');
  }
  if (Capacitor.isNativePlatform()) {
    document.body.classList.add('IS-CAPACITOR');
  }

  const userOnApp = async (platformType: 'ios' | 'android') => {
    let updateInfo = {};
    if (platformType === 'ios') {
      updateInfo = { hasIOSApp: true };
    } else {
      updateInfo = { hasAndroidApp: true };
    }
    await updateCurrentUserInfo(updateInfo);
  };

  return (
    <AssetLoadErrorBoundary>
      <SubscriptionProvider>
        <AIDrawerProvider>
          <SessionFeedbackProvider>
            <VideoViewerModalProvider>
              <GamepadProvider>
                <AnkiImportNotifierProvider>
                  {isBackendSwitcherEnabled() && <BackendOverrideBadge />}
                  <OfflineIndicator />
                  <AudioOverviewBarHost
                    pathname={pathname}
                    onOpenLibrary={openAudioOverviewLibrary}
                    onOpenArticle={openAudioOverviewArticle}
                  />
                  <AudioOverviewWatcher />
                  <PaymentIssueBanner subscription={subscription} />
                  <InstitutionalAccessModals />
                  <main id="main-content">
                    <RouterProvider router={router} />
                  </main>
                </AnkiImportNotifierProvider>
                <ClientVersionManager />
                <NativeAuthUrlListener />
                <ClientUpdateRequiredModal />
              </GamepadProvider>
              {currentUser && (
                <Suspense fallback={null}>
                  <ExplainerPopoversManager />
                </Suspense>
              )}
            </VideoViewerModalProvider>
          </SessionFeedbackProvider>
        </AIDrawerProvider>
      </SubscriptionProvider>
    </AssetLoadErrorBoundary>
  );
}

export default App;
